import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Calendar, DollarSign, ChevronDown, ChevronUp, Receipt, Wallet, TrendingUp, CreditCard, Trash2, Search } from 'lucide-react';
import financeService from '../services/financeService';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import toast from 'react-hot-toast';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const datePart = dateStr.split('T')[0];
  return datePart.split('-').reverse().join('/');
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

const FinanceExpenses = () => {
  const { user } = useAuth();
  const canManage = hasPermission(user?.role, 'financial:full') || hasPermission(user?.role, 'financial:expenses');
  const canView = canManage || hasPermission(user?.role, 'financial:view');
  const queryClient = useQueryClient();

  const getDefaultFilters = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    return { page: 1, limit: 20, search: '', category: '', payment_method: '', source_type: '', start_date: '', end_date: '' };
  };

  const [filters, setFilters] = useState(getDefaultFilters);

  const [expandedPayroll, setExpandedPayroll] = useState(true);
  const [expandedPeriods, setExpandedPeriods] = useState({});

  const { data: categoriesData } = useQuery({
    queryKey: ['finance-cashbox-categories'],
    queryFn: () => financeService.getCashboxCategories(),
    staleTime: 5 * 60 * 1000
  });
  const { data: methods } = useQuery({ queryKey: ['finance-methods'], queryFn: financeService.getPaymentMethods });

  const categories = categoriesData?.data || [];

  const { data, isLoading, error } = useQuery({
    queryKey: ['finance-expenses', filters],
    queryFn: () => {
      const params = {};
      Object.keys(filters).forEach(key => {
        if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
          params[key] = filters[key];
        }
      });
      return financeService.getExpenses(params);
    },
    keepPreviousData: true,
    staleTime: 0,
    refetchOnWindowFocus: true
  });

  const { data: totalsData } = useQuery({
    queryKey: ['finance-expenses-totals', filters],
    queryFn: () => {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.category) params.category = filters.category;
      if (filters.payment_method) params.payment_method = filters.payment_method;
      return financeService.getExpensesTotals(params);
    },
    staleTime: 0,
    refetchOnWindowFocus: true
  });

  const deleteMutation = useMutation({
    mutationFn: financeService.deleteExpense,
    onSuccess: () => {
      toast.success('Saída removida');
      queryClient.invalidateQueries(['finance-expenses']);
      queryClient.invalidateQueries(['finance-stats']);
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'Erro ao remover')
  });

  const items = Array.isArray(data?.data) ? data.data : [];

  const payrollItems = useMemo(() => items.filter(item => item.source_type === 'payslip'), [items]);
  const otherExpensesItems = useMemo(() => items.filter(item => item.source_type !== 'payslip'), [items]);

  const payrollByPeriod = useMemo(() => {
    const grouped = {};
    payrollItems.forEach(item => {
      const date = new Date(item.date);
      const periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!grouped[periodKey]) {
        grouped[periodKey] = {
          period: periodKey,
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          monthName: date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
          items: [],
          total: 0
        };
      }
      grouped[periodKey].items.push(item);
      grouped[periodKey].total += parseFloat(item.net_amount || 0);
    });
    return Object.values(grouped).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  }, [payrollItems]);

  const totalPayroll = useMemo(() => payrollItems.reduce((sum, item) => sum + parseFloat(item.net_amount || 0), 0), [payrollItems]);
  const totalOtherExpenses = useMemo(() => otherExpensesItems.reduce((sum, item) => sum + parseFloat(item.net_amount || 0), 0), [otherExpensesItems]);

  const togglePeriod = (periodKey) => setExpandedPeriods(prev => ({ ...prev, [periodKey]: !prev[periodKey] }));

  const expensesByCategory = useMemo(() => {
    const grouped = {};
    otherExpensesItems.forEach(item => {
      const cat = item.category_name || 'Sem categoria';
      if (!grouped[cat]) grouped[cat] = 0;
      grouped[cat] += parseFloat(item.net_amount || 0);
    });
    return Object.entries(grouped).map(([name, value]) => ({ name, value }));
  }, [otherExpensesItems]);

  const expensesByMonth = useMemo(() => {
    const grouped = {};
    const allItems = [...payrollItems, ...otherExpensesItems];
    allItems.forEach(item => {
      const date = new Date(item.expense_date || item.date);
      const monthKey = date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      if (!grouped[monthKey]) grouped[monthKey] = 0;
      grouped[monthKey] += parseFloat(item.net_amount || 0);
    });
    return Object.entries(grouped).map(([name, value]) => ({ name, value }));
  }, [payrollItems, otherExpensesItems]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters(getDefaultFilters());
  };

  if (!canView) return <div className="p-6 text-red-600">Sem permissão para visualizar saídas.</div>;

  return (
    <div className="p-6 space-y-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Saídas</h1>
          <p className="text-gray-500 mt-1">Visualize saídas de pagamentos</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="bg-white border-2 border-gray-100 rounded-2xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Pesquisar..."
              className="input pl-9"
              value={filters.search}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
            />
          </div>
          <select
            className="input"
            value={filters.category}
            onChange={(e) => handleFilterChange({ category: e.target.value })}
          >
            <option value="">Todas as Categorias</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <select
            className="input"
            value={filters.payment_method}
            onChange={(e) => handleFilterChange({ payment_method: e.target.value })}
          >
            <option value="">Todos os Métodos</option>
            {methods?.data?.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mt-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Data Início</label>
            <input
              type="date"
              className="input"
              value={filters.start_date}
              onChange={(e) => handleFilterChange({ start_date: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Data Fim</label>
            <input
              type="date"
              className="input"
              value={filters.end_date}
              onChange={(e) => handleFilterChange({ end_date: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Registros por página</label>
            <select
              className="input"
              value={filters.limit}
              onChange={(e) => handleFilterChange({ limit: parseInt(e.target.value) })}
            >
              <option value={10}>10 por página</option>
              <option value={20}>20 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
            </select>
          </div>
          <button
            onClick={handleClearFilters}
            className="btn-secondary h-10"
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="group relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 border-slate-500 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-white/30 to-white/10" />
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-200 mb-1">Total Histórico</p>
              <p className="text-3xl font-bold text-white tracking-tight">
                {formatCurrency(totalsData?.data?.historical_total || 0)}
              </p>
              <p className="text-xs text-slate-300 mt-2">{totalsData?.data?.historical_count || 0} registros</p>
            </div>
            <div className="p-3 bg-white/20 rounded-xl group-hover:bg-white/30 transition-colors">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-primary-600 to-primary-700 border-2 border-primary-500 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-white/30 to-white/10" />
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-primary-200 mb-1">Total Filtrado</p>
              <p className="text-3xl font-bold text-white tracking-tight">
                {formatCurrency(totalsData?.data?.filtered_total || 0)}
              </p>
              <p className="text-xs text-primary-300 mt-2">{totalsData?.data?.filtered_count || 0} registros</p>
            </div>
            <div className="p-3 bg-white/20 rounded-xl group-hover:bg-white/30 transition-colors">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Payroll Section - Collapsible */}
      {payrollItems.length > 0 && (
        <div className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden">
          <button
            onClick={() => setExpandedPayroll(!expandedPayroll)}
            className="w-full px-6 py-4 flex items-center justify-between bg-primary-50 hover:bg-primary-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Users className="w-5 h-5 text-primary-600" />
              </div>
              <div className="text-left">
                <h2 className="text-lg font-semibold text-gray-900">Folha de Pagamento</h2>
                <p className="text-sm text-gray-500">{payrollItems.length} holerites • {formatCurrency(totalPayroll)}</p>
              </div>
            </div>
            {expandedPayroll ? <ChevronUp className="w-5 h-5 text-primary-500" /> : <ChevronDown className="w-5 h-5 text-primary-500" />}
          </button>

          {expandedPayroll && (
            <div className="divide-y divide-gray-100">
              {payrollByPeriod.map(period => (
                <div key={period.period}>
                  <button
                    onClick={() => togglePeriod(period.period)}
                    className="w-full px-6 py-3 flex items-center justify-between bg-white hover:bg-primary-50 transition-colors border-l-4 border-transparent hover:border-primary-300"
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-700">{period.monthName}</span>
                      <span className="text-sm text-gray-400">({period.items.length})</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-primary-600">
                        {formatCurrency(period.total)}
                      </span>
                      {expandedPeriods[period.period] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </button>

                  {expandedPeriods[period.period] && (
                    <div className="bg-primary-50/30">
                      <table className="min-w-full">
                        <thead>
                          <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                            <th className="px-6 py-2">Funcionário</th>
                            <th className="px-4 py-2">Data</th>
                            <th className="px-4 py-2">Conta</th>
                            <th className="px-4 py-2 text-right">Valor</th>
                            <th className="px-4 py-2">Aprovado por</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {period.items.map(item => (
                            <tr key={item.id} className="hover:bg-white transition-colors">
                              <td className="px-6 py-3 font-medium text-gray-900">{item.party_name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(item.date)}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.account_name || '-'}</td>
                              <td className="px-4 py-3 text-right font-semibold text-primary-600">
                                {formatCurrency(item.net_amount)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-400">{item.created_by_name || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tabela de Despesas */}
      <div className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin" />
            <p className="text-gray-500 mt-3">Carregando despesas...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            <p>Erro ao carregar dados.</p>
          </div>
        ) : otherExpensesItems.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Receipt className="w-8 h-8 text-primary-300" />
            </div>
            <p className="text-gray-500 font-medium">Nenhuma despesa registrada</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-primary-50/50">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                    <th className="px-6 py-3">Data</th>
                    <th className="px-4 py-3">Categoria</th>
                    <th className="px-4 py-3">Descrição</th>
                    <th className="px-4 py-3">Método</th>
                    <th className="px-4 py-3">Conta</th>
                    <th className="px-4 py-3 text-right">Bruto</th>
                    <th className="px-4 py-3 text-right">Imposto</th>
                    <th className="px-4 py-3 text-right">Líquido</th>
                    {canManage && <th className="px-4 py-3 w-12" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {otherExpensesItems.map(item => {
                    const isBalanced = item.linked_transaction_id != null;
                    const isAbate = item.description && item.description.includes('/Abate');
                    return (
                    <tr key={item.id} className={`hover:bg-primary-50/30 transition-colors ${isBalanced ? 'bg-orange-50' : ''}`}>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(item.date)}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isBalanced ? 'bg-orange-100 text-orange-700' : 'bg-primary-100 text-primary-700'}`}>
                          {item.category_name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {item.source_type === 'cashbox_transaction' && item.project_id && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                            Projeto #{item.project_id}
                          </span>
                        )}
                        {isAbate && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 mr-2">
                            Abate
                          </span>
                        )}
                        <div className="font-medium text-gray-900">{item.description}</div>
                        {item.party_name && (
                          <div className="text-xs text-gray-500 mt-0.5">Fornecedor: {item.party_name}</div>
                        )}
                        {item.project_description && (
                          <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{item.project_description}</div>
                        )}
                        {isBalanced && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                            🔗 Balanceado
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isBalanced ? 'bg-orange-100 text-orange-700' : 'bg-primary-100 text-primary-700'}`}>
                          {isAbate ? item.description : (item.payment_method || 'N/A')}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500"> → </td>
                      <td className="px-4 py-4 text-sm text-right">{formatCurrency(item.gross_amount)}</td>
                      <td className="px-4 py-4 text-sm text-right text-gray-500">{(item.tax_percent || 0).toFixed(2)}%</td>
                      <td className="px-4 py-4 text-sm font-semibold text-right text-primary-600">
                        {formatCurrency(item.net_amount)}
                      </td>
                      {canManage && (
                        <td className="px-4 py-4">
                          {item.source_type === 'expense' ? (
                            <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" onClick={() => deleteMutation.mutate(item.id)}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          ) : item.source_type === 'cashbox_transaction' ? (
                            <span className="text-xs text-orange-600">Caixa</span>
                          ) : null}
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Paginação */}
            {data?.pagination?.pages > 1 && (
              <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Mostrando <span className="font-semibold">{(data.pagination.page - 1) * data.pagination.limit + 1}</span> a{' '}
                  <span className="font-semibold">{Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)}</span> de{' '}
                  <span className="font-semibold">{data.pagination.total}</span> registros
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleFilterChange({ page: filters.page - 1 })}
                    disabled={filters.page <= 1}
                    className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600">
                    Página {filters.page} de {data.pagination.pages}
                  </span>
                  <button
                    onClick={() => handleFilterChange({ page: filters.page + 1 })}
                    disabled={filters.page >= data.pagination.pages}
                    className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FinanceExpenses;
